import { DatePicker, Popover, Select } from "antd"
import { type RangePickerProps } from "antd/lib/date-picker"
import dayjs from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import Icon from "../../Icon"
import { useDisplayDateFormat } from "../../contexts/date-format.ts"
import { cn } from "../../utils/cn.ts"
import { DATE_FORMAT } from "../../utils/date/date.ts"

const { RangePicker } = DatePicker

interface DateRangeFilterProps<T> {
  after?: string | null
  before?: string | null
  /** The pair of params this range writes, so no caller rebuilds the patch. */
  afterKey: keyof T
  beforeKey: keyof T
  disabled?: boolean
  placeholder?: string
  /**
   * Sizing for the trigger. The default carries a `basis`, which a caller that
   * stacks this control under a label must override — inside a column flex
   * container `flex-basis` sets the height, and the field renders 15rem tall.
   */
  className?: string
  onChange: (patch: Partial<T>) => void
}

/**
 * A range picker that collapses to one closed `Select`, so it lines up with the
 * other controls in a filter row instead of taking two fields' worth of space.
 *
 * `group justify-start` is what keeps the collapsed control reading as one
 * field, so it is applied here rather than left to every caller to remember.
 */
export function DateRangeFilter<T>({
  after,
  before,
  afterKey,
  beforeKey,
  disabled = false,
  placeholder,
  className = "basis-60 w-60 max-md:grow",
  onChange
}: DateRangeFilterProps<T>) {
  const { t } = useTranslation()
  const displayFormat = useDisplayDateFormat()
  const [open, setOpen] = useState(false)

  const hasDateRange = !!after && !!before

  const emit = (start: string | null, end: string | null) =>
    onChange({ [afterKey]: start, [beforeKey]: end } as Partial<T>)

  const onClear = () => {
    emit(null, null)
    setOpen(false)
  }

  const onRangeChange: RangePickerProps["onChange"] = (dates) => {
    emit(
      dates?.[0]?.format(DATE_FORMAT) || null,
      dates?.[1]?.format(DATE_FORMAT) || null
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={
        <RangePicker
          value={hasDateRange ? [dayjs(after), dayjs(before)] : undefined}
          onChange={onRangeChange}
          format={displayFormat}
          placeholder={[t("filters.startDate"), t("filters.endDate")]}
        />
      }
      trigger="click"
    >
      <Select
        disabled={disabled}
        className={cn(className, "group justify-start")}
        allowClear
        onClear={onClear}
        prefix={<Icon type={"Calendar"} />}
        open={false}
        value={
          hasDateRange
            ? `${dayjs(after).format(displayFormat)} - ${dayjs(before).format(displayFormat)}`
            : null
        }
        placeholder={placeholder ?? t("filters.dateRangePlaceholder")}
      />
    </Popover>
  )
}
